import { useEffect, useState } from "react";

import { readPrimaryEnvironmentTarget } from "../environments/primary/target";
import { primaryEnvironmentFetch } from "./runtime";

/**
 * Server resources a page element shows — project favicons, image
 * attachments — as object URLs. The server answers its routes to a server
 * token alone, which an `<img>` cannot send, so the window fetches them with
 * its bearer and shows the bytes.
 *
 * Elements lease a URL. One fetch serves every lease, and the object URL is
 * revoked `IDLE_RELEASE_MS` after the last lease ends: a remounted element
 * reuses it, and a closed thread's images leave memory. A failed load is
 * forgotten, so a later lease asks again (the first one can run before the
 * window holds a token).
 */
const IDLE_RELEASE_MS = 60_000;

export interface ServerObjectUrlIo {
  readonly fetch: typeof fetch;
  readonly toObjectUrl: (blob: Blob) => string;
  readonly revoke: (objectUrl: string) => void;
}

const browserIo: ServerObjectUrlIo = {
  fetch: (input, init) => primaryEnvironmentFetch(input, init),
  toObjectUrl: (blob) => URL.createObjectURL(blob),
  revoke: (objectUrl) => URL.revokeObjectURL(objectUrl),
};

interface Entry {
  readonly load: Promise<string | null>;
  objectUrl: string | null;
  leases: number;
  idle: ReturnType<typeof setTimeout> | null;
}

const entries = new Map<string, Entry>();

/**
 * Whether a URL is the window's server's to answer. The token goes to that
 * origin alone; a `blob:` or `data:` URL is the page's own, and any other
 * origin is shown as it is.
 */
export function needsServerToken(url: string): boolean {
  if (url.startsWith("blob:") || url.startsWith("data:")) return false;
  const target = readPrimaryEnvironmentTarget();
  if (target === null) return false;
  try {
    return (
      new URL(url, target.target.httpBaseUrl).origin === new URL(target.target.httpBaseUrl).origin
    );
  } catch {
    return false;
  }
}

export interface ServerObjectUrlLease {
  readonly load: Promise<string | null>;
  readonly release: () => void;
}

export function leaseServerObjectUrl(
  url: string,
  io: ServerObjectUrlIo = browserIo,
): ServerObjectUrlLease {
  let entry = entries.get(url);
  if (entry === undefined) {
    const created: Entry = {
      load: io
        .fetch(url)
        .then(async (response) => (response.ok ? io.toObjectUrl(await response.blob()) : null))
        .catch(() => null)
        .then((objectUrl) => {
          created.objectUrl = objectUrl;
          if (objectUrl === null && entries.get(url) === created) entries.delete(url);
          return objectUrl;
        }),
      objectUrl: null,
      leases: 0,
      idle: null,
    };
    entries.set(url, created);
    entry = created;
  }
  const leased = entry;
  leased.leases += 1;
  if (leased.idle !== null) {
    clearTimeout(leased.idle);
    leased.idle = null;
  }
  let released = false;
  return {
    load: leased.load,
    release: () => {
      if (released) return;
      released = true;
      leased.leases -= 1;
      if (leased.leases > 0) return;
      leased.idle = setTimeout(() => {
        if (leased.leases > 0 || entries.get(url) !== leased) return;
        entries.delete(url);
        if (leased.objectUrl !== null) io.revoke(leased.objectUrl);
      }, IDLE_RELEASE_MS);
    },
  };
}

/** The object URL a lease already loaded, for a first render without a flash. */
function loadedObjectUrl(url: string): string | null {
  return entries.get(url)?.objectUrl ?? null;
}

/**
 * What an element shows for `url`: the URL itself when it needs no server
 * token, else the server resource's object URL once loaded, `null` until then
 * and on a failure.
 */
export function useServerObjectUrl(url: string | null | undefined): string | null {
  const serverUrl = url && needsServerToken(url) ? url : null;
  const [shown, setShown] = useState<{ readonly url: string; readonly objectUrl: string | null }>();

  useEffect(() => {
    if (serverUrl === null) return;
    const lease = leaseServerObjectUrl(serverUrl);
    let current = true;
    void lease.load.then((objectUrl) => {
      if (current) setShown({ url: serverUrl, objectUrl });
    });
    return () => {
      current = false;
      lease.release();
    };
  }, [serverUrl]);

  if (!url) return null;
  if (serverUrl === null) return url;
  return shown?.url === serverUrl ? shown.objectUrl : loadedObjectUrl(serverUrl);
}

export function resetServerObjectUrlsForTests(): void {
  for (const entry of entries.values()) {
    if (entry.idle !== null) clearTimeout(entry.idle);
  }
  entries.clear();
}
